import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import background from "../../../assets/images/giat_ui_2.jpg";
import { ButtonCustom } from "../../../components/custom/button-custom";
import { showToast } from "../../../components/custom/custom-toast";
import IconEye from "../../../components/icons/icon-eye";
import IconLockDots from "../../../components/icons/icon-lock-dots";
import IconMail from "../../../components/icons/icon-mail";
import { STRING_MENU } from "../../../constants/string";
import { useDataResponse } from "../../../hooks/useResponseHandler";
import { getDefaultLandingRoute } from "../../../hooks/usePermission";
import { setIsLogin } from "../../../store/authSlice";
import { setSidebarMenu } from "../../../store/themeConfigSlice";
import CookieUtils from "../../../utils/cookie-utils";
import { getDeviceUUID } from "../../../utils/helpers";
import { VerifyOtpComponent } from "../components/verify-otp-component";
import { AuthService, PublicService } from "../services/auth-service";

export const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // --- STATES ---
  const [userName, setUserName] = useState("");
  const [errorUserName, setErrorUserName] = useState("");
  const [errorUserNameVisible, setErrorUserNameVisible] = useState(false);

  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorPassword, setErrorPassword] = useState("");
  const [errorPasswordVisible, setErrorPasswordVisible] = useState(false);

  const [isVerifyForm, setIsVerifyForm] = useState(false);
  const [rememberPassword, setRememberPassword] = useState(false);

  // --- API HOOKS ---
  const {
    fetch: onLogin,
    response: responsesLogin,
    loading: loadingLogin,
  } = AuthService.postLogin();

  const {
    fetch: onLoginVerify,
    response: responsesLoginVerify,
    loading: isLoadingVerify,
  } = AuthService.postLoginVerify();

  const { fetch: onSetting, response: responseSetting } =
    PublicService.setting();

  // --- LOGIC XỬ LÝ ĐĂNG NHẬP (VALIDATION) ---
  const handleLogin = () => {
    let isValid = true;

    if (!userName || userName.trim() === "") {
      setErrorUserName("Vui lòng nhập tên đăng nhập");
      setErrorUserNameVisible(true);
      isValid = false;
    } else {
      setErrorUserNameVisible(false);
    }

    if (!password || password.trim() === "") {
      setErrorPassword("Vui lòng nhập mật khẩu");
      setErrorPasswordVisible(true);
      isValid = false;
    } else {
      setErrorPasswordVisible(false);
    }

    if (!isValid) return;

    onLogin({
      username: userName.toLocaleLowerCase(),
      password: btoa(password),
    });
  };

  // --- API RESPONSE HANDLERS ---
  useDataResponse(responsesLogin, (data) => {
    onSetting();
    if (data?.is_2fa == 0) {
      onLoginVerify({
        username: userName.toLocaleLowerCase(),
        password: btoa(password),
        device_uuid: getDeviceUUID(),
        otp: "",
      });
    } else {
      setIsVerifyForm(true);
    }
  });

  useDataResponse(responseSetting, (data) => {
    // Lưu CONFIG_RESOURCE_URL - không stringify vì đây là string đơn giản
    if (data.CONFIG_RESOURCE_URL) {
      CookieUtils.setCookie(
        "CONFIG_RESOURCE_URL",
        data.CONFIG_RESOURCE_URL,
        rememberPassword ? 24 * 30 : 12
      );
    }
    // Lưu CONFIG_REALTIME_URL để SocketContext sử dụng - không stringify
    if (data.CONFIG_REALTIME_URL) {
      CookieUtils.setCookie(
        "CONFIG_REALTIME_URL",
        data.CONFIG_REALTIME_URL,
        rememberPassword ? 24 * 30 : 12
      );
    }
  });

  useDataResponse(responsesLoginVerify, (response) => {
    response.totp_qr_code = "";
    // privilege_codes đã nằm trong cookie "user" (usePermission đọc từ đó),
    // nên không cần lưu thêm cookie "privilegeCodes" trùng lặp.
    CookieUtils.setCookie(
      "user",
      JSON.stringify(response),
      rememberPassword ? 24 * 30 : 12
    );
    dispatch(setIsLogin(true));
    dispatch(setSidebarMenu(STRING_MENU.DASHBOARD));
    showToast({ message: "Đăng nhập thành công", type: "success" });
    navigate(getDefaultLandingRoute());
  });

  const callApiLoginVerify = (otpCode: string) => {
    onLoginVerify({
      username: userName.toLocaleLowerCase(),
      password: btoa(password),
      device_uuid: getDeviceUUID(),
      otp: otpCode,
    });
  };

  // --- MAIN RENDER ---
  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      {/* Background Image - Full Screen */}
      <div className="absolute inset-0">
        <img
          src={background}
          alt="Laundry Background"
          className="h-full w-full object-cover"
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/90 via-emerald-800/80 to-teal-900/90" />

        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-green-400/10 rounded-full blur-2xl" />
      </div>

      {/* Content Container */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="w-full max-w-5xl flex flex-col lg:flex-row items-center gap-8 lg:gap-16">

          {/* Left Side - Branding */}
          <div className="flex-1 text-center lg:text-left text-white space-y-6 lg:space-y-8 px-4">
            {/* Logo */}
            <div className="inline-flex items-center justify-center lg:justify-start">
              <div className="bg-white/10 backdrop-blur-xl p-4 rounded-2xl border border-white/20 shadow-2xl">
                <img
                  className="w-40 lg:w-48"
                  src="/static/media/logo_laundry.c31e8aab0e3f91f8f4b0.png"
                  alt="G24H Logo"
                />
              </div>
            </div>

            {/* Headline */}
            <div className="space-y-4">
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-black tracking-tight leading-tight">
                <span className="bg-gradient-to-r from-white via-emerald-100 to-teal-200 bg-clip-text text-transparent">
                  Quản lý thông minh
                </span>
                <br />
                <span className="text-white/90">Vận hành hiệu quả</span>
              </h1>
              <p className="text-lg lg:text-xl text-emerald-100/80 max-w-lg mx-auto lg:mx-0 leading-relaxed">
                Hệ thống quản lý dịch vụ giặt là chuyên nghiệp, mang lại sự tin cậy và tốc độ cho doanh nghiệp của bạn.
              </p>
            </div>

            {/* Features */}
            <div className="hidden lg:flex flex-wrap gap-4 pt-4">
              {[
                { icon: "✨", text: "Quản lý đơn hàng" },
                { icon: "📊", text: "Báo cáo chi tiết" },
                { icon: "🚚", text: "Theo dõi giao hàng" },
                { icon: "💳", text: "Thanh toán đa dạng" },
              ].map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/10"
                >
                  <span>{feature.icon}</span>
                  <span className="text-sm font-medium text-white/90">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="w-full max-w-md">
            {/* Glass Card */}
            <div className="bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl p-8 lg:p-10 border border-white/50">
              {isVerifyForm ? (
                <VerifyOtpComponent
                  setValueOtp={callApiLoginVerify}
                  isLoading={isLoadingVerify}
                  backWardForm={() => setIsVerifyForm(false)}
                />
              ) : (
                <div className="space-y-8">
                  {/* Form Header */}
                  <div className="text-center space-y-2">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg shadow-emerald-500/30 mb-2">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">
                      Đăng nhập
                    </h2>
                    <p className="text-gray-500">
                      Chào mừng bạn quay trở lại!
                    </p>
                  </div>

                  {/* Form */}
                  <form
                    className="space-y-6"
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleLogin();
                    }}
                  >
                    {/* Username Field */}
                    <div className="space-y-2">
                      <label
                        htmlFor="username"
                        className={`block text-sm font-semibold transition-colors ${
                          errorUserNameVisible ? "text-red-500" : "text-gray-700"
                        }`}
                      >
                        Tên đăng nhập
                      </label>
                      <div className="relative group">
                        <span
                          className={`absolute inset-y-0 left-0 flex items-center pl-4 transition-colors ${
                            errorUserNameVisible
                              ? "text-red-400"
                              : "text-gray-400 group-focus-within:text-emerald-500"
                          }`}
                        >
                          <IconMail fill={false} className="w-5 h-5" />
                        </span>
                        <input
                          id="username"
                          type="text"
                          value={userName}
                          onChange={(e) => {
                            setUserName(e.target.value);
                            if (errorUserNameVisible && e.target.value.trim() !== "")
                              setErrorUserNameVisible(false);
                          }}
                          onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                          placeholder="Nhập tên đăng nhập"
                          className={`w-full py-3.5 pl-12 pr-4 rounded-xl border-2 outline-none transition-all duration-200 font-medium
                            ${
                              errorUserNameVisible
                                ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
                                : "border-gray-200 bg-gray-50 focus:border-emerald-500 focus:bg-white focus:ring-4 focus:ring-emerald-500/10"
                            }
                          `}
                          autoComplete="off"
                        />
                      </div>
                      {errorUserNameVisible && (
                        <p className="flex items-center gap-1.5 text-sm text-red-500 font-medium animate-pulse">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {errorUserName}
                        </p>
                      )}
                    </div>

                    {/* Password Field */}
                    <div className="space-y-2">
                      <label
                        htmlFor="password"
                        className={`block text-sm font-semibold transition-colors ${
                          errorPasswordVisible ? "text-red-500" : "text-gray-700"
                        }`}
                      >
                        Mật khẩu
                      </label>
                      <div className="relative group">
                        <span
                          className={`absolute inset-y-0 left-0 flex items-center pl-4 transition-colors ${
                            errorPasswordVisible
                              ? "text-red-400"
                              : "text-gray-400 group-focus-within:text-emerald-500"
                          }`}
                        >
                          <IconLockDots fill={false} className="w-5 h-5" />
                        </span>
                        <input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            if (errorPasswordVisible && e.target.value.trim() !== "")
                              setErrorPasswordVisible(false);
                          }}
                          onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                          placeholder="Nhập mật khẩu"
                          className={`w-full py-3.5 pl-12 pr-12 rounded-xl border-2 outline-none transition-all duration-200 font-medium
                            ${
                              errorPasswordVisible
                                ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
                                : "border-gray-200 bg-gray-50 focus:border-emerald-500 focus:bg-white focus:ring-4 focus:ring-emerald-500/10"
                            }
                          `}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <IconEye isIconOff={showPassword} />
                        </button>
                      </div>
                      {errorPasswordVisible && (
                        <p className="flex items-center gap-1.5 text-sm text-red-500 font-medium animate-pulse">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {errorPassword}
                        </p>
                      )}
                    </div>

                    {/* Remember Me */}
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-3 cursor-pointer group">
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={rememberPassword}
                            onChange={(e) => setRememberPassword(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-5 h-5 border-2 border-gray-300 rounded-md peer-checked:bg-emerald-500 peer-checked:border-emerald-500 transition-all duration-200 flex items-center justify-center">
                            {rememberPassword && (
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </div>
                        <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                          Ghi nhớ đăng nhập
                        </span>
                      </label>
                      <button
                        type="button"
                        className="text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors"
                      >
                        Quên mật khẩu?
                      </button>
                    </div>

                    {/* Submit Button */}
                    <ButtonCustom
                      isLoading={loadingLogin}
                      text="Đăng nhập"
                      className="w-full py-4 rounded-xl text-base font-bold text-white
                        bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500
                        hover:from-emerald-700 hover:via-emerald-600 hover:to-teal-600
                        shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50
                        hover:-translate-y-0.5 active:translate-y-0 active:shadow-md
                        transition-all duration-300 flex justify-center items-center gap-2"
                      onClick={handleLogin}
                    />
                  </form>

                  {/* Footer */}
                  <div className="pt-6 border-t border-gray-100">
                    <p className="text-center text-sm text-gray-400">
                      &copy; 2024 G24H Laundry Management System
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom decoration for mobile */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-emerald-950/50 to-transparent lg:hidden" />
    </div>
  );
};
